/**
 * Statewide energy *_format row selection (Rev2).
 * Logical key: normalized GEOID × calendar year (sector is fixed per table fetch).
 * Precedence: direct utility observed > other observed > utility-derived backcast/extrapolation >
 * NREL / model-average stack rows without utility measurements.
 */
import { canonicalInventoryGeofipsKey } from './ghgInventoryGeofipsCanonical';
import { isDirectUtilityObservedDataSource } from './energyFormatUtilitySources';

export type EnergyFormatSelectionReason =
  | 'direct_utility_observed'
  | 'observed_non_nrel'
  | 'utility_derived_extrapolation_or_county_backcast'
  | 'nrel_city_profiles_observed_label'
  | 'interpolated'
  | 'estimated_non_nrel'
  | 'nrel_modeled_estimate'
  | 'commercial_model_stack'
  | 'residual_model_or_null';

export type EnergyFormatValueColumn = 'total_therms' | 'total_kwh';

export function normalizeEnergyFormatGeofips(raw: unknown): string {
  return canonicalInventoryGeofipsKey(raw);
}

/** Los Angeles residential electricity: city-only rows pending LADWP adjudication (do not invent place twins). */
export function isLosAngelesResidentialElecPendingAdjudication(
  geofips: unknown,
  table?: string
): boolean {
  if (table && table !== 'elec_res_format') return false;
  return normalizeEnergyFormatGeofips(geofips) === '644000';
}

function strField(row: Record<string, unknown>, key: string): string {
  const v = row[key];
  return v == null ? '' : String(v).trim();
}

function activityValue(row: Record<string, unknown>, valueCol: EnergyFormatValueColumn): number | null {
  const v = row[valueCol];
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function scoreEnergyFormatRow(
  row: Record<string, unknown>,
  valueCol: EnergyFormatValueColumn = 'total_therms'
): { tier: number; reason: EnergyFormatSelectionReason } {
  const measurement = strField(row, 'measurement').toLowerCase();
  const methodNote = strField(row, 'method_note').toLowerCase();
  const dataSource = strField(row, 'data_source');
  const extrapolationModel = strField(row, 'extrapolation_model').toUpperCase();
  const activity = activityValue(row, valueCol);
  const hasActivity = activity != null && activity > 0;

  const utilityObserved = isDirectUtilityObservedDataSource(dataSource, measurement);

  let tier = 5;
  let reason: EnergyFormatSelectionReason = 'residual_model_or_null';

  if (utilityObserved && hasActivity) {
    tier = 55;
    reason = 'direct_utility_observed';
  } else if (measurement === 'observed' && !methodNote.includes('nrel')) {
    tier = 50;
    reason = 'observed_non_nrel';
  } else if (methodNote.includes('extrapol') || methodNote.includes('county ratio')) {
    tier = 45;
    reason = 'utility_derived_extrapolation_or_county_backcast';
  } else if (measurement === 'observed' && methodNote.includes('nrel')) {
    tier = 40;
    reason = 'nrel_city_profiles_observed_label';
  } else if (measurement.includes('interpol') || methodNote.includes('interpol')) {
    tier = 30;
    reason = 'interpolated';
  } else if (measurement === 'estimated' && dataSource && !dataSource.toUpperCase().includes('NREL')) {
    tier = 25;
    reason = 'estimated_non_nrel';
  } else if (measurement === 'estimated' && methodNote.includes('nrel')) {
    tier = 15;
    reason = 'nrel_modeled_estimate';
  } else if (extrapolationModel && extrapolationModel !== 'OBSERVED') {
    tier = 10;
    reason = 'commercial_model_stack';
  }

  if (!hasActivity && extrapolationModel && extrapolationModel !== 'OBSERVED') {
    tier = Math.min(tier, 8);
    reason = 'commercial_model_stack';
  }

  return { tier, reason };
}

function geographyRank(row: Record<string, unknown>): number {
  const gt = strField(row, 'geography_type').toLowerCase();
  return { place: 4, city: 3, county: 2, state: 1 }[gt] ?? 0;
}

function geofipsCanonicalStorageMatch(row: Record<string, unknown>): number {
  const stored = strField(row, 'geofips').replace(/\.0$/, '');
  const norm = normalizeEnergyFormatGeofips(stored);
  if (!norm) return 0;
  return stored === norm || stored.replace(/^0+/, '') === norm ? 1 : 0;
}

export function compareEnergyFormatRows(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
  valueCol: EnergyFormatValueColumn = 'total_therms'
): number {
  const sa = scoreEnergyFormatRow(a, valueCol);
  const sb = scoreEnergyFormatRow(b, valueCol);
  const actA = activityValue(a, valueCol);
  const actB = activityValue(b, valueCol);
  const hasA = actA != null && actA > 0 ? 1 : 0;
  const hasB = actB != null && actB > 0 ? 1 : 0;

  const keyA = [sa.tier, hasA, geographyRank(a), geofipsCanonicalStorageMatch(a), actA ?? 0] as const;
  const keyB = [sb.tier, hasB, geographyRank(b), geofipsCanonicalStorageMatch(b), actB ?? 0] as const;

  for (let i = 0; i < keyA.length; i += 1) {
    if (keyA[i] !== keyB[i]) return keyA[i] > keyB[i] ? 1 : -1;
  }
  return 0;
}

export function pickEnergyFormatWinner<T extends Record<string, unknown>>(
  candidates: T[],
  valueCol: EnergyFormatValueColumn = 'total_therms'
): { winner: T | null; reason: EnergyFormatSelectionReason; ambiguous: boolean } {
  if (!candidates.length) {
    return { winner: null, reason: 'residual_model_or_null', ambiguous: false };
  }
  let winner = candidates[0];
  for (let i = 1; i < candidates.length; i += 1) {
    const c = candidates[i];
    if (compareEnergyFormatRows(c, winner, valueCol) > 0) winner = c;
  }
  const ties = candidates.filter((c) => compareEnergyFormatRows(c, winner, valueCol) === 0);
  const { reason } = scoreEnergyFormatRow(winner, valueCol);
  return { winner, reason, ambiguous: ties.length > 1 };
}

export function selectEnergyFormatWinnersByYear<T extends Record<string, unknown>>(
  rows: T[],
  valueCol: EnergyFormatValueColumn = 'total_therms'
): {
  byYear: Map<number, T>;
  reasons: Map<number, EnergyFormatSelectionReason>;
} {
  const byYear = new Map<number, T[]>();
  for (const row of rows) {
    const year = Number(row.year);
    if (!Number.isFinite(year)) continue;
    const bucket = byYear.get(year) ?? [];
    bucket.push(row);
    byYear.set(year, bucket);
  }

  const winners = new Map<number, T>();
  const reasons = new Map<number, EnergyFormatSelectionReason>();
  for (const [year, group] of byYear.entries()) {
    const { winner, reason } = pickEnergyFormatWinner(group, valueCol);
    if (winner) {
      winners.set(year, winner);
      reasons.set(year, reason);
    }
  }
  return { byYear: winners, reasons };
}

export function dedupeEnergyFormatRowsByYear<T extends Record<string, unknown>>(
  rows: T[],
  valueCol: EnergyFormatValueColumn = 'total_therms'
): T[] {
  const { byYear } = selectEnergyFormatWinnersByYear(rows, valueCol);
  return Array.from(byYear.entries())
    .sort(([a], [b]) => a - b)
    .map(([, r]) => r);
}

export function energyFormatWinnerDebugMeta(
  row: Record<string, unknown>,
  reason: EnergyFormatSelectionReason,
  valueCol: EnergyFormatValueColumn = 'total_therms'
): Record<string, unknown> {
  return {
    year: row.year,
    therms_or_kwh: activityValue(row, valueCol),
    measurement: row.measurement,
    data_source: row.data_source,
    geography_type: row.geography_type,
    raw_geofips: row.geofips,
    norm_geofips: normalizeEnergyFormatGeofips(row.geofips),
    selected_reason: reason,
  };
}
